import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { handleActionError } from "@/lib/errors";
import {
  changedCounts,
  stockItemTitle,
  stockItemVariantLabel
} from "@/lib/stock-item";
import { useUpsertStockTracking } from "@/queries/customer/stock-tracking";
import type { StoreStockTrackingItem } from "@repo/api-sdk";
import { toast } from "@medusajs/ui";
import { TruncateTooltip } from "@repo/design-system";
import { ImageOff } from "lucide-react";
import { memo, useEffect, useState } from "react";

interface UpdateCountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The items just reordered - offer to refresh their on-hand counts. */
  items: StoreStockTrackingItem[];
  /**
   * Called after the prompt is dismissed (Save or Skip), so the caller can move
   * on - e.g. the hero routes to the cart. Fires on both paths so navigation is
   * not tied to whether counts were edited.
   */
  onComplete: () => void;
}

/**
 * The light post-reorder count prompt (#72 / #73). After a reorder is added to
 * the cart, a salon has just committed to restocking - the honest way to clear
 * the "due" flag is for them to update the physical on-hand count, which stays
 * manual (never inferred from an order). This is an *optional* nudge: it seeds
 * each input with the current count, saves only the ones that changed (a
 * full-state upsert per edited item), and Skip is a first-class path. Both Save
 * and Skip call `onComplete`, so the caller (e.g. the hero) can then route on.
 */
export const UpdateCountsDialog = memo(function UpdateCountsDialog({
  open,
  onOpenChange,
  items,
  onComplete
}: UpdateCountsDialogProps) {
  const upsert = useUpsertStockTracking();
  const [counts, setCounts] = useState<Record<string, string>>({});

  // Seed each input from the current on-hand every time the prompt opens.
  useEffect(() => {
    if (open) {
      setCounts(
        Object.fromEntries(
          items.map((item) => [
            item.variant_id,
            item.on_hand === null ? "" : String(item.on_hand)
          ])
        )
      );
    }
  }, [open, items]);

  const done = () => {
    onOpenChange(false);
    onComplete();
  };

  const handleSave = async () => {
    if (upsert.isPending) {
      return;
    }

    // Only the items whose count actually changed, each with its parsed new
    // on-hand - the stock-item module owns the "blank -> null, invalid -> keep"
    // rule so the prompt upserts only what the salon really edited.
    const edits = changedCounts(items, counts);

    if (edits.length === 0) {
      done();
      return;
    }

    try {
      await Promise.all(
        edits.map(({ item, on_hand }) =>
          upsert.mutateAsync({
            variant_id: item.variant_id,
            reorder_level: item.reorder_level,
            on_hand
          })
        )
      );
      toast.success(
        `Updated ${edits.length} ${edits.length === 1 ? "count" : "counts"}`
      );
      done();
    } catch (err) {
      toast.error(
        handleActionError(err, {
          boundary: "update counts",
          fallback: "We couldn't update your stock counts. Please try again."
        })
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update your counts?</DialogTitle>
          <DialogDescription>
            Added to your cart. If you&apos;ve already restocked these, set your
            on-hand count so we stop flagging them - your counts stay yours to
            manage.
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-[45vh] min-w-0 space-y-1 overflow-y-auto py-2">
          {items.map((item) => {
            const title = stockItemTitle(item);
            const variantLabel = stockItemVariantLabel(item);
            const thumbnail = item.variant?.product?.thumbnail ?? null;
            const inputId = `count-${item.variant_id}`;
            return (
              <li
                key={item.variant_id}
                className="flex min-w-0 items-center gap-3 rounded-md p-2"
              >
                <div className="border-border flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-gray-50 dark:bg-neutral-900">
                  <ProductThumbnailImage
                    src={thumbnail}
                    alt=""
                    className="h-full w-full object-cover"
                    fallback={
                      <ImageOff
                        className="h-4 w-4 text-gray-300 dark:text-gray-600"
                        aria-hidden="true"
                      />
                    }
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <TruncateTooltip
                    as="p"
                    className="text-sm font-medium text-gray-900 dark:text-white"
                  >
                    {title}
                  </TruncateTooltip>
                  {variantLabel ? (
                    <TruncateTooltip
                      as="p"
                      className="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                    >
                      {variantLabel}
                    </TruncateTooltip>
                  ) : null}
                </div>
                <label
                  htmlFor={inputId}
                  className="flex flex-shrink-0 flex-col text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                >
                  On hand
                  <Input
                    id={inputId}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={counts[item.variant_id] ?? ""}
                    placeholder="—"
                    aria-label={`On hand count for ${
                      variantLabel ? `${title} (${variantLabel})` : title
                    }`}
                    disabled={upsert.isPending}
                    onChange={(event) =>
                      setCounts((prev) => ({
                        ...prev,
                        [item.variant_id]: event.target.value
                      }))
                    }
                    className="mt-1 h-9 w-20"
                  />
                </label>
              </li>
            );
          })}
        </ul>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={done}
            disabled={upsert.isPending}
          >
            Skip for now
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={upsert.isPending}
          >
            {upsert.isPending ? "Saving…" : "Save counts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
